import { Analyse, OHNE_TAETIGKEIT } from "../state/schema";
import { berechneHaushalt, summeSparbeitraege, summeVersicherungsbeitraege } from "./calc";
import { ageFromISO, formatEUR, isValidEmail, isValidPhone, isValidPLZ, parseAmount } from "./format";

/* Section-Validatoren (§42/§43). Geben Fehlermeldungen zurück (leer = ok).
   Werden sowohl im Schritt-Gate als auch beim finalen Absenden genutzt. */

export function vZiele(a: Analyse): string[] {
  const e: string[] = [];
  if (a.ziele.wichtig.length === 0) e.push("Bitte wähle mindestens einen Punkt aus, der dir wichtig ist.");
  if (a.ziele.ziele.length === 0) e.push("Bitte wähle mindestens ein Ziel aus.");
  return e;
}

export function vStammdaten(a: Analyse): string[] {
  const e: string[] = [];
  const s = a.stammdaten;
  const req: [string, string][] = [
    [s.vorname, "Vorname"], [s.nachname, "Nachname"], [s.geburtsdatum, "Geburtsdatum"],
    [s.geburtsort, "Geburtsort"], [s.staatsangehoerigkeit, "Staatsangehörigkeit"],
    [s.strasse, "Straße"], [s.hausnummer, "Hausnummer"], [s.plz, "Postleitzahl"],
    [s.ort, "Ort"], [s.email, "E-Mail-Adresse"], [s.mobil, "Mobilnummer"],
    [s.familienstand, "Familienstand"],
  ];
  for (const [val, name] of req) if (!String(val).trim()) e.push(`Bitte ${name} angeben.`);
  if (s.email && !isValidEmail(s.email)) e.push("Bitte eine gültige E-Mail-Adresse angeben.");
  if (s.mobil && !isValidPhone(s.mobil)) e.push("Bitte eine gültige Mobilnummer angeben.");
  if (s.plz && !isValidPLZ(s.plz)) e.push("Die Postleitzahl sollte 5 Ziffern haben.");
  if (s.geburtsdatum) {
    const age = ageFromISO(s.geburtsdatum);
    if (age === null) e.push("Bitte ein gültiges Geburtsdatum angeben.");
    else if (age < 0 || age > 120) e.push("Bitte ein plausibles Geburtsdatum angeben.");
  }
  return e;
}

/* Kinderfrage – wird im Schritt „Beruf & Familie" gestellt und geprüft (nicht im
   Schritt „Persönliche Angaben"). */
export function vKinder(a: Analyse): string[] {
  const e: string[] = [];
  const s = a.stammdaten;
  if (!s.hatKinder) e.push("Bitte angeben, ob du Kinder hast.");
  if (s.hatKinder === "ja") {
    if (!String(s.anzahlKinder).trim()) e.push("Bitte die Anzahl der Kinder angeben.");
    if (!String(s.alterKinder).trim()) e.push("Bitte das Alter der Kinder angeben.");
  }
  return e;
}

/* Berufliche Situation – Pflichtangaben, weil Status, Tätigkeit und Abschluss die
   Beratungsgrundlage bilden (Einkommensverlauf, Absicherungsbedarf). Die Berufs-
   bezeichnung entfällt nur, wenn jemand aktuell gar nicht berufstätig ist. */
export function vBeruf(a: Analyse): string[] {
  const e: string[] = [];
  const s = a.stammdaten;
  if (!String(s.berufsstatus ?? "").trim()) e.push("Bitte deinen aktuellen beruflichen Status angeben.");
  if (s.berufsstatus !== OHNE_TAETIGKEIT && !String(s.berufsbezeichnung ?? "").trim())
    e.push("Bitte deine genaue Berufsbezeichnung bzw. aktuelle Tätigkeit angeben.");
  if (!String(s.abschluss ?? "").trim()) e.push("Bitte deinen Schul- oder Studienabschluss angeben.");
  return e;
}

/** Schritt „Beruf & Familie" prüft Kinder- und Berufsangaben gemeinsam. */
export function vBerufFamilie(a: Analyse): string[] {
  return [...vKinder(a), ...vBeruf(a)];
}

export function vGesundheit(a: Analyse): string[] {
  const e: string[] = [];
  const g = a.gesundheit;
  if (!g.koerperlich) e.push("Bitte die Frage zur körperlichen Gesundheit beantworten.");
  if (!g.psychisch) e.push("Bitte die Frage zur psychischen Gesundheit beantworten.");
  if (!g.raucher) e.push("Bitte angeben, ob du rauchst.");
  if (!g.risikohobby) e.push("Bitte die Frage zu risikoreichen Hobbys beantworten.");
  if (g.risikohobby === "ja" && !g.risikohobbyText.trim())
    e.push("Bitte kurz angeben, welche Hobbys du ausübst.");
  return e;
}

export function vAltersvorsorge(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.altersvorsorge.vorhanden) e.push("Bitte die Frage zur Altersvorsorge beantworten.");
  if (a.altersvorsorge.vorhanden === "ja") {
    if (a.altersvorsorge.vertraege.length === 0) e.push("Bitte mindestens einen Vertrag erfassen.");
    a.altersvorsorge.vertraege.forEach((v, i) => {
      if (!v.vertragsart.trim()) e.push(`Vertrag ${i + 1}: Bitte die Vertragsart wählen.`);
      if (!v.anbieterUnbekannt && !v.anbieter.trim()) e.push(`Vertrag ${i + 1}: Bitte Anbieter angeben oder „weiß ich nicht".`);
      if (!v.beitragUnbekannt && !v.beitrag.trim()) e.push(`Vertrag ${i + 1}: Bitte Beitrag angeben oder „weiß ich nicht".`);
    });
  }
  return e;
}

export function vArbeitskraft(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.arbeitskraft.vorhanden) e.push("Bitte die Frage zur Arbeitskraftabsicherung beantworten.");
  if (a.arbeitskraft.vorhanden === "ja") {
    if (a.arbeitskraft.vertraege.length === 0) e.push("Bitte mindestens eine Absicherung erfassen.");
    a.arbeitskraft.vertraege.forEach((v, i) => {
      if (!v.vertragsart.trim()) e.push(`Absicherung ${i + 1}: Bitte die Vertragsart wählen.`);
    });
  }
  return e;
}

export function vSach(a: Analyse): string[] {
  const e: string[] = [];
  if (a.sachversicherungen.auswahl.length === 0)
    e.push('Bitte wähle aus, welche Versicherungen bestehen (oder „Keine").');
  return e;
}

export function vVermoegen(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.vermoegen.vorhanden) e.push("Bitte die Frage zum Vermögen beantworten.");
  if (a.vermoegen.vorhanden === "ja" && a.vermoegen.posten.length === 0)
    e.push("Bitte mindestens eine Vermögenskategorie auswählen.");
  return e;
}

export function vStrom(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.strom.eigenerVertrag) e.push("Bitte die Stromfrage beantworten.");
  if (a.strom.eigenerVertrag === "ja") {
    if (!a.strom.anbieter.trim()) e.push("Bitte den Stromanbieter angeben.");
    if (!String(a.strom.abschlag).trim()) e.push("Bitte den monatlichen Abschlag (Strom) angeben.");
    const ok = a.dokumente.some((d) => d.kategorie === "Stromabrechnung" && d.status === "done");
    if (!ok) e.push("Bitte deine letzte Stromabrechnung hochladen.");
  }
  return e;
}

export function vGas(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.gas.eigenerVertrag) e.push("Bitte die Gasfrage beantworten.");
  if (a.gas.eigenerVertrag === "ja") {
    if (!a.gas.anbieter.trim()) e.push("Bitte den Gasanbieter angeben.");
    if (!String(a.gas.abschlag).trim()) e.push("Bitte den monatlichen Abschlag (Gas) angeben.");
    const ok = a.dokumente.some((d) => d.kategorie === "Gasabrechnung" && d.status === "done");
    if (!ok) e.push("Bitte deine letzte Gasabrechnung hochladen.");
  }
  return e;
}

export function vKrankenversicherung(a: Analyse): string[] {
  const e: string[] = [];
  const k = a.krankenversicherung;
  if (!k.gesetzlichVersichert) e.push("Bitte angeben, ob du gesetzlich krankenversichert bist.");
  if (k.gesetzlichVersichert === "ja") {
    if (!String(k.anbieter ?? "").trim()) e.push("Bitte den Namen deiner Krankenkasse angeben.");
    if (!String(k.halt ?? "").trim()) e.push("Bitte angeben, was dich aktuell bei deiner Krankenkasse hält.");
  }
  return e;
}

export function vEinkommen(a: Analyse): string[] {
  const e: string[] = [];
  const k = a.einkommen;
  const req: [string, string][] = [
    [k.bruttoMonat, "Bruttoeinkommen"], [k.nettoMonat, "Nettoeinkommen"],
    [k.nebeneinkuenfte, "Nebeneinkünfte"], [k.kindergeld, "Kindergeld"],
    [k.weitereEinnahmen, "weitere verfügbare Einnahmen"],
  ];
  for (const [val, name] of req)
    if (String(val).trim() === "") e.push(`Bitte ${name} angeben (0 €, wenn nicht vorhanden).`);
  if (k.gehaltszahlungen === "andere" && !String(k.gehaltszahlungenAndere).trim())
    e.push("Bitte die Anzahl der Gehaltszahlungen angeben.");
  // Einkommensnachweis (§18)
  if (!a.einkommensnachweis.art) e.push("Bitte auswählen, welchen Einkommensnachweis du hochlädst.");
  if (a.einkommensnachweis.art === "keine" && !a.einkommensnachweis.begruendung.trim())
    e.push("Bitte kurz begründen, warum aktuell keine Gehaltsabrechnung vorliegt.");
  if (a.einkommensnachweis.art === "jahresendabrechnung" || a.einkommensnachweis.art === "gehaltsabrechnung") {
    const ok = a.dokumente.some((d) => d.kategorie === "Einkommensnachweis" && d.status === "done");
    if (!ok) e.push("Bitte mindestens einen Einkommensnachweis erfolgreich hochladen.");
  }
  return e;
}

export function vAusgaben(a: Analyse): string[] {
  const e: string[] = [];
  const g = a.ausgaben;
  const felder: [string, string][] = [
    [g.wohnen, "Wohnkosten"], [g.lebenshaltung, "Lebenshaltungskosten"],
    [g.mobilitaet, "Mobilität"], [g.kreditraten, "Kreditraten"],
    [g.unterhalt, "Unterhalt"], [g.versicherungen, "Versicherungsbeiträge"],
    [g.abosVereine, "Abonnements & Vereine"], [g.sonstige, "sonstige Ausgaben"],
    [g.sparbeitraege, "bestehende Sparbeiträge"],
  ];
  for (const [val, name] of felder)
    if (String(val).trim() === "") e.push(`Bitte ${name} angeben (0 € möglich).`);

  // Auto-übernommene Sparbeiträge (aus Vermögens-Posten)
  const sumS = summeSparbeitraege(a);
  const spar = parseAmount(g.sparbeitraege);
  if (spar < sumS - 0.005)
    e.push(`Deine bestehenden Sparbeiträge dürfen nicht geringer sein als deine oben erfassten Sparprodukte (${formatEUR(sumS)}).`);
  if (spar > sumS + 0.005 && String(g.sparZielText ?? "").trim() === "")
    e.push("Bitte kurz angeben, wohin dein zusätzlicher Sparbetrag fließt.");

  // Auto-übernommene Versicherungsbeiträge (aus AV + AKS + Sachversicherungen)
  const sumV = summeVersicherungsbeitraege(a);
  const vers = parseAmount(g.versicherungen);
  if (vers < sumV - 0.005)
    e.push(`Deine Versicherungsbeiträge dürfen nicht geringer sein als deine oben erfassten Verträge (${formatEUR(sumV)}).`);
  if (vers > sumV + 0.005 && String(g.versZielText ?? "").trim() === "")
    e.push("Bitte kurz angeben, welche zusätzlichen Versicherungen dein höherer Beitrag abdeckt.");
  return e;
}

export function vZukunft(a: Analyse): string[] {
  const e: string[] = [];
  const z = a.zukunft;
  if (String(z.mindestens).trim() === "") e.push("Bitte den monatlichen Mindestbetrag angeben (0 € möglich).");
  if (String(z.komfortabel).trim() === "") e.push("Bitte den maximalen Betrag angeben (0 € möglich).");
  if (String(z.einmalig).trim() === "") e.push("Bitte den einmalig verfügbaren Betrag angeben (0 € möglich).");
  return e;
}

export function vPraeferenzen(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.verantwortung) e.push("Bitte die Verantwortungsfrage beantworten.");
  if (!a.praeferenz) e.push("Bitte angeben, was für dich am schwersten zu akzeptieren wäre.");
  return e;
}

export function vBerater(a: Analyse): string[] {
  return a.beraterId ? [] : ["Bitte deinen persönlichen Berater auswählen."];
}

export function vEinwilligungen(a: Analyse): string[] {
  const e: string[] = [];
  const c = a.einwilligungen;
  if (!c.angabenGeprueft) e.push("Bitte bestätige, dass du deine Angaben geprüft hast.");
  if (!c.datenschutz) e.push("Bitte die Kenntnisnahme der Datenschutzerklärung bestätigen.");
  if (!c.verarbeitung) e.push("Bitte der Verarbeitung deiner Angaben zustimmen.");
  if (!c.uebermittlung) e.push("Bitte der Übermittlung an Blacksmith Finance zustimmen.");
  if (!c.weiterleitung) e.push("Bitte der Weiterleitung an deinen Berater zustimmen.");
  if (!c.driveSpeicherung) e.push("Bitte der Speicherung der Dokumente im geschützten Drive zustimmen.");
  if (!c.gesundheit) e.push("Bitte der Verarbeitung deiner Gesundheitsangaben gesondert zustimmen.");
  return e;
}

/* ---- Freundliche Plausibilitätswarnungen (nicht blockierend, §43) ---- */
export function plausibilityWarnings(a: Analyse): string[] {
  const w: string[] = [];
  const brutto = parseAmount(a.einkommen.bruttoMonat);
  const netto = parseAmount(a.einkommen.nettoMonat);
  if (brutto > 0 && netto > brutto)
    w.push("Dein Nettoeinkommen ist höher als dein Bruttoeinkommen. Bitte prüfe die Werte kurz.");
  const h = berechneHaushalt(a);
  if (parseAmount(a.ausgaben.wohnen) === 0)
    w.push("Du hast keine Wohnkosten angegeben. Falls das zutrifft, ist alles in Ordnung.");
  if (h.einnahmenGesamt > 0 && h.laufendeAusgaben > h.einnahmenGesamt)
    w.push("Deine Ausgaben liegen über deinen Einnahmen. Das ist okay – wir schauen uns das gemeinsam an.");
  return w;
}

export function investitionsWarnung(a: Analyse): string | null {
  const h = berechneHaushalt(a);
  const komfort = parseAmount(a.zukunft.komfortabel);
  if (h.freiVerfuegbar > 0 && komfort > h.freiVerfuegbar * 1.2) {
    return "Dein gewünschter Betrag liegt spürbar über deinem aktuell frei verfügbaren Betrag. Das ist kein Problem – dein Berater bespricht mögliche Wege gern mit dir.";
  }
  return null;
}

/** Aggregierte Validierung für das finale Absenden. */
export function validateAll(a: Analyse): string[] {
  return [
    ...vZiele(a), ...vStammdaten(a), ...vKinder(a), ...vBeruf(a), ...vGesundheit(a), ...vAltersvorsorge(a),
    ...vArbeitskraft(a), ...vSach(a), ...vVermoegen(a), ...vStrom(a), ...vGas(a),
    ...vKrankenversicherung(a), ...vEinkommen(a), ...vAusgaben(a), ...vZukunft(a), ...vPraeferenzen(a),
    ...vBerater(a), ...vEinwilligungen(a),
  ];
}
